import ReceiptIcon from '@mui/icons-material/Receipt';
import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import ErrorSection from 'components/ErrorSection/ErrorSection';
import EulaAgreement from 'components/EulaAgreement/EulaAgreement';
import ShortcutListener from 'components/KeyboardShortcuts/ShortcutListener';
import OnboardingTour from 'components/OnboardingTour/OnboardingTour';
import { ticketsShortcutMap } from 'config/keyboardShortcuts';
import { useOnboardingTour } from 'contexts/OnboardingTourContext';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { StorageE } from 'declarations/enums/storage';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { buildShortcutBindings } from 'helpers/shortcuts';
import { getCurrentUserId } from 'helpers/user';
import { columns } from 'pages/TicketsPage/components/ColumnPickerSettingsPopup/components/ColumnPickerSettings/mockedColumns';

import { getUsersLatestEulaAgreementStatus } from '../../api/eula';

import CardRow from './components/BannerMessages/BannerMessages';
import CloseTicketPopup from './components/CloseTicketPopup/CloseTicketPopup';
import ColumnPickerSettingsPopup from './components/ColumnPickerSettingsPopup/ColumnPickerPopup';
import DealerCard from './components/DealerCard/DealerCard';
import DealerReport from './components/DealerReport/DealerReport';
import Header from './components/Header/Header';
import { HorizontallyCollapsibleCard } from './components/HorizontallyCollapsibleCard/HorizontallyCollapsibleCard';
import ImportSchedulePopup from './components/ImportSchedulePopup/ImportSchedulePopup';
import InsightsContent from './components/InsightsTabContent/InsightsContent';
import OpenNewTicketPopup from './components/OpenNewTicketPopup/OpenNewTicketPopup';
import ReassignTicketPopup from './components/ReassignTicketPopup/ReassignTicketPopup';
import CombinedScheduleCards from './components/ScheduleCards/CombinedScheduleCards';
import ScheduleListViewHeader from './components/ScheduleListViewHeader/ScheduleListViewHeader';
import ShareTicketPopup from './components/ShareTicketPopup/ShareTicketPopup';
import TicketsCardView from './components/TicketsCardView/TicketsCardView';
import TicketsListView from './components/TicketsListView/TicketsListView';
import useTicketsRefinement from './components/TicketsRefinement/hooks/useTicketsRefinement';
import useTicketsPage from './hooks/useTicketsPage';
import { useTicketsPageShortcuts } from './hooks/useTicketsPageShortcuts';
import {
  BannerAndCardsRowStyled,
  BannerCardsRowStyled,
  BannerDealerReportColumnStyled,
  CardsRowCollapsibleStyled,
  CardsRowLayoutWrapperStyled,
  CardsRowSectionStyled,
  ContentWrapStyled,
  DealerCardWrapStyled,
  DealerReportRowWrapStyled,
  FilterBtnStyled,
  HeaderBoxStyled,
  MessageWrapStyled,
  TableContentWrapStyled,
  TicketsPageStyled,
} from './TicketsPage.styled';

const TicketsPage: FC = (): JSX.Element => {
  const { isMobile } = useResponsive();
  const {
    ticketsPages,
    ticketsCount,
    isLoadingTickets,
    isTicketsFetchingError,
    fetchTicketsErrorMessage,
    fetchTicketsValidationErrorMessage,
    activeTab,
    isScheduleTicketList,
    setIsScheduleTicketList,
    selectedRoofTop,
    getSearchParam,
    ticketListScrollContainerRef,
    ticketListScrollPosition,
    setTicketListScrollPosition,
  } = useTickets();
  const tableContentRef = useRef<HTMLDivElement>(null);
  const { handleSelectTicket, handleSaveChanges, formMethods } = useTicketsPage({ ticketsPages });
  const shortcutHandlers = useTicketsPageShortcuts({ handleSaveChanges, formMethods });
  const shortcutBindings = useMemo(
    () => buildShortcutBindings(ticketsShortcutMap.shortcuts, shortcutHandlers),
    [shortcutHandlers]
  );

  const { handleClearAllFiltersClick } = useTicketsRefinement();
  const { isRunningTour, steps, handleJoyrideCallback } = useOnboardingTour();
  const [displayEulaAgreement, setDisplayEulaAgreement] = useState(false);
  const [isCardsRowExpanded, setIsCardsRowExpanded] = useState((): boolean => {
    try {
      const stored = localStorage.getItem(StorageE.CardsRowExpanded);
      if (stored !== null) return stored === 'true';
    } catch {
      // Ignore storage errors
    }
    return !isMobile;
  });

  useEffect((): void => {
    try {
      localStorage.setItem(StorageE.CardsRowExpanded, String(isCardsRowExpanded));
    } catch {
      // Ignore storage errors
    }
  }, [isCardsRowExpanded]);
  const userId = getCurrentUserId();

  useEffect(() => {
    if (activeTab !== ActiveTabTypeE.Schedule) {
      setIsScheduleTicketList(false);
    } else {
      const hasScheduleFileInUrl = getSearchParam(TicketsRefinementKeysE.ScheduleFileId) != null;
      setIsScheduleTicketList(hasScheduleFileInUrl);
    }
  }, [activeTab, getSearchParam, setIsScheduleTicketList]);

  useEffect(() => {
    if (tableContentRef.current && activeTab !== ActiveTabTypeE.Insights) {
      tableContentRef.current.scrollTop = 0;
    }
  }, [selectedRoofTop, activeTab]);

  useEffect(() => {
    const el = ticketListScrollContainerRef.current;
    if (isMobile && el && ticketListScrollPosition > 0 && !isLoadingTickets) {
      requestAnimationFrame(() => {
        el.scrollTop = ticketListScrollPosition;
        setTicketListScrollPosition(0);
      });
    }
  }, [
    isMobile,
    ticketListScrollPosition,
    setTicketListScrollPosition,
    isLoadingTickets,
    ticketListScrollContainerRef,
  ]);

  if (isTicketsFetchingError && userId) {
    getUsersLatestEulaAgreementStatus(userId)
      .then((statusResponse): void => {
        if (!statusResponse) {
          setDisplayEulaAgreement(true);
        } else {
          setDisplayEulaAgreement(false);
        }
      })
      .catch((): void => {
        // Error fetching EULA agreement
      });
  }

  if (isTicketsFetchingError) {
    if (displayEulaAgreement) {
      return <EulaAgreement />;
    }

    return (
      <>
        <Header onSaveChanges={handleSaveChanges} formMethods={formMethods} />
        <MessageWrapStyled>
          <FilterBtnStyled
            size="md"
            variant="outline-rounded"
            onClick={handleClearAllFiltersClick}
            title="Reset all filters and sorting"
          >
            Clear All Filters
          </FilterBtnStyled>
          <ErrorSection
            errorDetails={fetchTicketsErrorMessage || fetchTicketsValidationErrorMessage}
          />
        </MessageWrapStyled>
      </>
    );
  }

  return (
    <TicketsPageStyled>
      <ShortcutListener bindings={shortcutBindings} />
      <ContentWrapStyled ref={isMobile ? ticketListScrollContainerRef : undefined}>
        <HeaderBoxStyled>
          {activeTab !== ActiveTabTypeE.Insights && (
            <CardsRowSectionStyled $expanded={isCardsRowExpanded}>
              <CardsRowCollapsibleStyled $expanded={isCardsRowExpanded}>
                <CardsRowLayoutWrapperStyled>
                  <CardRow />
                  <BannerAndCardsRowStyled>
                    <DealerCardWrapStyled $isSchedule={activeTab === ActiveTabTypeE.Schedule}>
                      <DealerCard />
                    </DealerCardWrapStyled>
                    <BannerDealerReportColumnStyled>
                      <BannerCardsRowStyled>
                        {activeTab !== ActiveTabTypeE.Schedule && (
                          <HorizontallyCollapsibleCard
                            title="Billing Periods"
                            storageKey={StorageE.BillingCardCollapsed}
                            collapsedIcon={<ReceiptIcon />}
                            disableOnMobile
                            isMobile={isMobile}
                          >
                            <DealerReportRowWrapStyled>
                              <DealerReport isReportOpened />
                            </DealerReportRowWrapStyled>
                          </HorizontallyCollapsibleCard>
                        )}
                        {activeTab === ActiveTabTypeE.Schedule && !isScheduleTicketList && (
                          <CombinedScheduleCards />
                        )}
                      </BannerCardsRowStyled>
                    </BannerDealerReportColumnStyled>
                  </BannerAndCardsRowStyled>
                </CardsRowLayoutWrapperStyled>
              </CardsRowCollapsibleStyled>
            </CardsRowSectionStyled>
          )}
          {activeTab === ActiveTabTypeE.Schedule && isScheduleTicketList && (
            <ScheduleListViewHeader />
          )}
          {activeTab !== ActiveTabTypeE.Insights && (
            <Header
              onSaveChanges={handleSaveChanges}
              formMethods={formMethods}
              isCardsRowExpanded={isCardsRowExpanded}
              onToggleCardsRow={(): void => setIsCardsRowExpanded(!isCardsRowExpanded)}
            />
          )}
        </HeaderBoxStyled>
        {((): JSX.Element => {
          if (activeTab === ActiveTabTypeE.Insights) return <InsightsContent />;
          if (isMobile) {
            return (
              <TableContentWrapStyled ref={tableContentRef}>
                <TicketsCardView
                  isLoadingTickets={isLoadingTickets}
                  ticketsPages={ticketsPages}
                  onSelectTicket={handleSelectTicket}
                  onSaveChanges={handleSaveChanges}
                  formMethods={formMethods}
                />
              </TableContentWrapStyled>
            );
          }
          return (
            <TableContentWrapStyled ref={tableContentRef}>
              <TicketsListView
                columns={columns}
                isLoadingTickets={isLoadingTickets}
                ticketsCount={ticketsCount}
                ticketsPages={ticketsPages}
                onSelectTicket={handleSelectTicket}
                onSaveChanges={handleSaveChanges}
                formMethods={formMethods}
              />
            </TableContentWrapStyled>
          );
        })()}
      </ContentWrapStyled>
      <OpenNewTicketPopup />
      <ReassignTicketPopup />
      <ShareTicketPopup />
      <ImportSchedulePopup />
      <CloseTicketPopup />
      <OnboardingTour
        hideCloseButton
        disableOverlayClose
        disableScrollParentFix
        continuous
        showSkipButton
        callback={handleJoyrideCallback}
        run={isRunningTour}
        steps={steps}
      />
      <ColumnPickerSettingsPopup activeTab={activeTab} />
    </TicketsPageStyled>
  );
};

export default TicketsPage;
